// src/encounter/intelligence/catalog-rail.tsx
//
// `?x=intel-rail-catalog` (ON on the dev hosts, OFF elsewhere; OFF = intelligence-rail.tsx unchanged): the Scrips
// Intelligence rail as Antonin's approved catalog rail, consumed as React components from @scripsteam/signal (the Signal
// catalog, scrips-signal-ds feat/signal ff5af208 + 850c5184 + fc326b6d). Nothing here is drawn by the app: the header
// (orb, "Scrips Intelligence", "· <phase>", More, Close Intelligence), the body groups (Prepared, Asked), the cards' shell
// (title and time, "<origin> · How this was produced", Read lines, body or the one-line state, Limits row, actions), the
// result rows, the chip row, Ask and the footer sentence are all his components. The app's only markup is the mount's
// layout wrapper.
//
// The data is the engine's, through the SAME hooks and safety rules as the OFF rail: the DoH §3.4 notice gate
// (acknowledgement-gate.tsx) shows alone until confirmed; Next best action (use-next-step.ts) with its decision buttons
// read-only unless the `decisionButtons` switch; Explain (use-explain.ts) from the footer chip; 401 / 429 / 504 / technical
// lines; an error boundary keyed on the patient. The adapters (catalog-adapters.ts) map payloads to props; every clinical
// string is an engine field verbatim. His fixture cards (Brief, Reconcile, Facts, …) are never rendered. Nothing logs.
import { useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { MoreHorizontal } from 'lucide-react';
import { Actions, Button, Fact, Group, IntelligenceRail, IntelligenceRailCard as Card, LabRow, ResultTable } from '@scripsteam/signal';
import { Skeleton, TextField } from '@scripsteam/scrips-signal-ds';
import { isEncounterFlagOn } from '@/shared/lib/encounter-flags';
import { SignalScope } from '../nurse-med-rec/nurse-med-rec-block';
import { usePreVisitRailWidth } from './pre-visit-rail-width';
import { AcknowledgementGate } from './acknowledgement-gate';
import { IntelligenceBoundary } from './intelligence-cards';
import { RailMenu } from './rail-menu';
import { N } from './next-step-classes';
import { CANCEL_AFTER_MS, type ExplainRequest } from './explain-panel';
import { answerView, criticalRowsOf, d45, SESSION_EXPIRED, STATE_LINE, WORDS, type ExplanationV1, type ResultRowView } from './explanation';
import { ACTION_LABEL, DECISION_LINE, REASON_MAX, type DecisionAction, type NextStepItem, type NextStepV1 } from './next-step';
import { useNextStep, type DecisionState } from './use-next-step';
import { useExplain, type ExplainSystem } from './use-explain';
import { useBrief } from './use-brief';
import { routeFor } from './chip-routes';
import { useIntelligenceRun } from '../intelligence-engine';
import { answerBlocks } from './ask-answer';
import { ASK_AGENT, ASK_STATE_LINE, ASK_WORDS, WITHHELD_PLAIN, type AskRequest } from './ask-words';
import { askKeyOf, markAskSent, wasAskSent } from './sent-asks';
import { cardStateOf, criticalRowsOf as criticalRowsOfRun, traceOf } from './run-card-state';
import { IntelRailCloseContext, IntelRailSignContext } from './intel-rail-close';
import { useRole } from '@/auth/use-role';
import { EXPLAIN_SYSTEMS, RAIL_WORDS, answerTime, railScreenLabel, systemLabel, type IntelligencePhase } from './rail-words';
import { askProduced, catalogOrigin, catalogScreen, type CatalogProduced, explainProduced, explainState, labResultOf, limitsGroupsOf, nextStepProduced, noticeState, recordItemOf, railScreenPlan, catalogOrbState, citesDetail } from './catalog-adapters';
import type { OrbState as DsOrbState } from '@scripsteam/scrips-signal-ds';

/** The body systems "Explain a body system" offers: only those the engine explains (rail-words.ts EXPLAIN_SYSTEMS). */
const SYSTEM_LABELS = EXPLAIN_SYSTEMS.map((s) => s.label);
/** His brief card's title (IntelligenceRail.tsx BriefCard); the engine's brief payload carries none. */
const BRIEF_TITLE = 'Pre-visit brief';
/**
 * A card with no engine answer yet (loading, a refusal line) still draws his "How this was produced" link. Its panel must
 * never fall back to the catalog's fixture checks ("passed", a model name, an output id): nothing was produced, so it lists
 * nothing.
 */
const NOTHING_PRODUCED: CatalogProduced = { checks: [], trace: [] };

export interface CatalogIntelligenceRailProps {
  phase: IntelligencePhase;
  /** Null, or the engine off for this deploy: the rail is drawn with no engine cards. */
  patientId: string | null;
  encounterId?: string | null;
  engineOn: boolean;
  explainRequest: ExplainRequest;
  /** The footer chip's choice (Heart & vessels: the one system the engine explains today). */
  onExplain?: (system: ExplainSystem) => void;
  onOpenResults?: () => void;
  /** The header tab showing (`encounter` | `terrain` | `chart` | `timeline` | `documents`): the rail serves that screen. */
  tab?: string | null;
  /** Ask (#910): the Composer's question. Absent (the `ask` switch off, or no engine): Send hands nothing on. */
  onAsk?: (question: string) => void;
  askRequest?: AskRequest | null;
  /** The visit's appointment id; Explain sends it beside the encounter id (#924: a just-arrived encounter). */
  appointmentId?: string | null;
  /** The ONE live Orb's state (orb-state.ts orbStateForAmbient: what the ambient scribe is doing). Absent: still. */
  orbState?: DsOrbState;
}

export function CatalogIntelligenceRail({ phase, patientId, encounterId = null, engineOn, explainRequest, onExplain, onOpenResults, tab = null, onAsk: hostAsk, askRequest = null, appointmentId = null, orbState }: CatalogIntelligenceRailProps) {
  const onClose = useContext(IntelRailCloseContext);
  const onReviewSign = useContext(IntelRailSignContext);
  // Next best action proposes orders: the engine reads it for a physician only (decisionsHttp.ts PRESCRIBER). A nurse
  // or allied-health clinician never asks for it, so the rail shows the cards that role may see instead of a refusal
  // (VPS demo, 4 Nov: as mariam the rail read "Scrips Intelligence is not available" from next-step 403).
  const prescriber = useRole().clinicalRole === 'physician';
  // The rail serves the screen showing (SAMER RULINGS, 4 Nov, rail lock §3): its label, chips and cards (railScreenPlan).
  const label = railScreenLabel(tab, phase);
  const plan = railScreenPlan(label);
  // `layoutFixes`: in the pre-visit's inline right column the rail takes the column's width (pre-visit-rail-width.ts).
  const fitWidth = usePreVisitRailWidth(isEncounterFlagOn('layoutFixes'));
  const screen = catalogScreen(phase);
  // Ask is the visit's (Visit = Next best action + Ask); elsewhere his Composer is inert.
  const onAsk = plan.ask ? hostAsk : undefined;
  // The code-built brief: the pre-visit prepares it on arrival (Antonin's PreRail: Next best action + Brief); "Prepare"
  // (chip-routes.ts) asks again with a new seq.
  const [briefSeq, setBriefSeq] = useState(1);
  // Each chip and lens goes where the owner's routing table sends it (chip-routes.ts). A route with no hook here yet
  // (rules, retest, absence, not-prepared) does nothing: no card is drawn for it.
  const onPick = (label: string) => {
    const route = routeFor(screen, label);
    if (route.kind === 'explain') onExplain?.('heart-vessels');
    else if (route.kind === 'code' && route.source === 'brief') setBriefSeq((n) => n + 1);
  };
  // "Explain a body system" offers only the systems the engine explains (EXPLAIN_SYSTEMS), and Explain runs the one picked.
  const onPickSystem = (label: string) => {
    const s = EXPLAIN_SYSTEMS.find((x) => x.label === label);
    if (s) onExplain?.(s.id);
  };
  const live = engineOn && !!patientId;
  // `gated` is the engine part of the column: the notice gate's line or form until it is confirmed, then the cards. Ambient is
  // OUT of the rail (SAMER RULINGS, 4 Nov): no session, recordings or diagnosis suggestions are ever drawn here.
  const rail = (gated: ReactNode, confirmed: boolean, asked?: ReactNode) => (
    // Main's SignalScope (#920): the vendored Signal sheets, with Antonin's tokens scoped to the rail, never the whole app.
    // Layout glue only: the column's height for his rail.
    <SignalScope slot="intelligence-rail-catalog" className="flex h-full min-h-0 flex-col">
      <IntelligenceRail
        screen={plan.screen}
        phase={label}
        height="100%"
        width={fitWidth}
        nbaRows
        onClose={onClose}
        // The orb shows what the ambient scribe is doing (recording, processing); never a constant.
        orbState={catalogOrbState(orbState)}
        // Reset, Settings and "how it works" have no handler here: his More menu is not drawn (rail lock §4).
        hideMore
        // Ask's limit (ask-words.ts), as today's rail footer holds it.
        askMaxLength={ASK_WORDS.maxLength}
        // Rail lock §3: the screen's chips only; "Explain a body system" is Terrain's alone (railScreenPlan).
        chips={plan.chips}
        systemList={SYSTEM_LABELS}
        onPick={onExplain && confirmed ? onPick : undefined}
        onPickSystem={onExplain && confirmed ? onPickSystem : undefined}
        onAsk={live && confirmed ? onAsk : undefined}
        // No Ask behind the box (the `ask` switch off, the engine off, the notice not yet confirmed): his Composer is inert.
        askDisabled={!(live && confirmed && onAsk)}
        // Wrap-up's "Review and sign" goes to the note's real Sign (the shell's action); none, no link.
        onReviewSign={onReviewSign}
        prepared={gated || undefined}
        asked={asked}
      />
    </SignalScope>
  );
  if (!live) return rail(null, false);
  return (
    // DoH §3.4: the engine cards show only once the clinician has confirmed the current notice.
    <AcknowledgementGate
      frame={(gated, confirmed) =>
        rail(
          gated,
          confirmed,
          confirmed ? (
            <IntelligenceBoundary key={patientId}>
              {onAsk ? <CatalogAsk key={`ask-${patientId}`} patientId={patientId} request={askRequest} onOpenResults={onOpenResults} /> : null}
              {/* Pre-visit: the code-built brief on arrival and on "Prepare". The visit-planning agent (pre-visit-brief) is not
                  run here: measured 4 Nov at 31 s on dev, and it holds the clinician's one-run lock, so Explain would 429. */}
              {plan.brief ? <CatalogBrief key={`brief-${patientId}`} patientId={patientId} encounterId={encounterId} seq={briefSeq} /> : null}
              {/* Terrain only: the body-system card, with the visit's appointment id beside the encounter id (PR 924). */}
              {plan.explain ? <CatalogExplain key={patientId} patientId={patientId} encounterId={encounterId} appointmentId={appointmentId} request={explainRequest} onOpenResults={onOpenResults} /> : null}
            </IntelligenceBoundary>
          ) : undefined,
        )
      }
    >
      {/* Terrain hides Next best action but keeps it mounted (Codex on PR 959): its decision, retry state and idempotency key
          survive a visit to Terrain. */}
      {prescriber ? (
        <div hidden={!plan.nextStep}>
          <IntelligenceBoundary key={patientId}>
            <CatalogNextStep key={`next-step-${patientId}`} patientId={patientId} encounterId={encounterId} />
          </IntelligenceBoundary>
        </div>
      ) : null}
    </AcknowledgementGate>
  );
}

/** A Retry under a one-line state (his one-line states carry no action of their own). */
function RetryRow({ label = 'Retry', onRetry }: { label?: string; onRetry: () => void }) {
  return (
    <Actions>
      <Button size="sm" data-action="retry" onClick={onRetry}>
        {label}
      </Button>
    </Actions>
  );
}

// ── Next best action ─────────────────────────────────────────────────────────────────────────────────────────
export function CatalogNextStep({ patientId, encounterId = null, decisionsEnabled }: { patientId: string; encounterId?: string | null; decisionsEnabled?: boolean }) {
  const ns = useNextStep({ patientId, encounterId });
  const [buttons] = useState(() => decisionsEnabled ?? isEncounterFlagOn('decisionButtons'));
  const { load } = ns;
  useEffect(() => {
    void load();
  }, [load]);
  if (ns.status !== 'done') return <Skeleton className="h-16" aria-label="Loading" />;
  const conflict = Object.values(ns.decisions).some((d) => d.status === 'conflict');
  if (!ns.card) {
    const line = ns.notice ?? SESSION_EXPIRED;
    const state = noticeState(line);
    return (
      <>
        <Card type="proposal" title={RAIL_WORDS.nextStepTitle} origin="rule" read={[]} produced={NOTHING_PRODUCED} state={state ?? 'answered'}>
          {state ? null : <Fact>{line}</Fact>}
        </Card>
        {line !== SESSION_EXPIRED ? <RetryRow onRetry={() => void ns.load()} /> : null}
      </>
    );
  }
  const card: NextStepV1 = ns.card;
  return (
    <>
      <Card
        type="proposal"
        title={RAIL_WORDS.nextStepTitle}
        time={answerTime(card.asOf) || undefined}
        origin={catalogOrigin(card.origin.kind)}
        read={[card.readLine]}
        produced={nextStepProduced(card)}
        // His `empty` state drops the Limits row; an empty card is drawn answered, so the not-checked line always shows.
        limits={<Fact>{card.notCheckedLine}</Fact>}
      >
        {!card.items.length && card.emptyLine ? <Fact>{card.emptyLine}</Fact> : null}
        {card.items.map((item) => (
          <NextStepItemRows key={item.id} item={item} decision={ns.decisions[item.id]} onDecide={ns.decide} onRetry={ns.retry} buttons={buttons} />
        ))}
        {/* Engine FM rows: how to draw a listed test, each line verbatim with its source id and the sentence it rests on. */}
        {card.sequencing?.map((s) => (
          <div key={s.id} data-slot="next-step-sequencing" data-item-id={s.id}>
            {s.cites.length ? (
              <Fact source={s.cites.join(', ')} detail={citesDetail(s.cites)}>
                {s.text}
              </Fact>
            ) : (
              <Fact>{s.text}</Fact>
            )}
            <Fact>{`${s.source.id} · checked ${s.source.checkedOn}: “${s.source.quote}”`}</Fact>
          </div>
        ))}
      </Card>
      {/* Codex P2 (OFF rail): a decision conflict asks the clinician to reload the card. */}
      {conflict ? <RetryRow label="Reload" onRetry={() => void ns.load()} /> : null}
    </>
  );
}

const MORE: readonly DecisionAction[] = ['defer', 'already-done'];

function NextStepItemRows({ item, decision, onDecide, onRetry, buttons }: { item: NextStepItem; decision: DecisionState | undefined; onDecide: (id: string, a: DecisionAction, reason?: string) => boolean; onRetry: (id: string) => boolean; buttons: boolean }) {
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState('');
  const closed = decision?.status === 'sending' || decision?.status === 'recorded' || decision?.status === 'conflict';
  const off = !buttons || closed;
  const press = (a: DecisionAction) => {
    if (a === 'not-indicated') return setAsking(true);
    setAsking(false);
    onDecide(item.id, a);
  };
  const more = MORE.filter((a) => item.actions.includes(a));
  const moreLabel = `More: ${more.map((a) => ACTION_LABEL[a]).join(', ')}`;
  const e = item.evidence;
  return (
    <div data-slot="next-step-item" data-item-id={item.id}>
      <Fact>{item.title}</Fact>
      {e ? (
        <Fact>
          <a href={e.link} target="_blank" rel="noopener noreferrer">
            {e.title}
          </a>
          {` ${e.journal} ${e.year}`}
          {[e.pmid ? `PMID ${e.pmid}` : null, e.doi ? `DOI ${e.doi}` : null].filter(Boolean).map((id) => ` · ${id}`)}
        </Fact>
      ) : (
        <Fact>{item.evidenceLine}</Fact>
      )}
      <Fact>{item.reviewLine}</Fact>
      {/* With the decisionButtons switch off the decisions route is not stamped: the same controls, drawn and disabled. */}
      <Actions>
        {item.actions.includes('add-to-note') ? (
          <Button variant="primary" size="sm" data-action="add-to-note" disabled={off} onClick={() => press('add-to-note')}>
            {ACTION_LABEL['add-to-note']}
          </Button>
        ) : null}
        {item.actions.includes('not-indicated') ? (
          <Button size="sm" data-action="not-indicated" disabled={off} onClick={() => press('not-indicated')}>
            {ACTION_LABEL['not-indicated']}
          </Button>
        ) : null}
        {more.length ? (
          <RailMenu
            label={moreLabel}
            disabled={off}
            hostClassName={N.moreHost}
            menuClassName={N.menu}
            itemClassName={N.menuItem}
            items={more.map((a) => ({ key: a, label: ACTION_LABEL[a], action: a, onPick: () => press(a) }))}
            trigger={<Button variant="quiet" size="sm" icon={<MoreHorizontal />} aria-label={moreLabel} data-action="more" />}
          />
        ) : null}
      </Actions>
      {buttons && asking && !closed ? (
        <form
          onSubmit={(ev) => {
            ev.preventDefault();
            if (onDecide(item.id, 'not-indicated', reason)) setAsking(false);
          }}
        >
          <TextField label="Reason (required)" required maxLength={REASON_MAX} value={reason} onChange={(ev) => setReason(ev.target.value)} />
          <Actions>
            <Button type="submit" variant="primary" size="sm" disabled={!reason.trim()}>
              Record
            </Button>
          </Actions>
        </form>
      ) : null}
      {decision?.status === 'recorded' ? <Fact>{DECISION_LINE.recorded(decision.request.action)}</Fact> : null}
      {decision?.line ? <Fact>{decision.line}</Fact> : null}
      {buttons && decision?.status === 'retryable' ? <RetryRow label="Try again" onRetry={() => onRetry(item.id)} /> : null}
    </div>
  );
}

// ── Ask (#910, behind its own `ask` switch: the state interpreter through POST /api/intelligence/run) ──────────
/** An engine agent a card runs instead of Ask's state interpreter (chip-routes.ts AGENTS), with its card title. */
export interface CatalogAgentRun {
  agent: string;
  actionKind: string;
  /** The rail tab the run is asked from (the engine's `origin.tab`). */
  tab: string;
  title: string;
}

export function CatalogAsk({ patientId, request, onOpenResults, agent, fallback }: { patientId: string; request: AskRequest | null; onOpenResults?: () => void; agent?: CatalogAgentRun; /** Drawn under the card when the run fails (not withheld, not denied). */ fallback?: ReactNode }) {
  const run = useIntelligenceRun();
  const { start } = run;
  const seq = request?.seq ?? 0;
  const question = request?.question ?? '';
  // Ask: the clinician's free question to the state interpreter. A chip's agent: its label is the curated question (`chip`).
  const ask = useCallback(
    () =>
      void start(
        agent
          ? { agent: agent.agent, actionKind: agent.actionKind, question, patientId, origin: { tab: agent.tab, question, chip: true } }
          : { ...ASK_AGENT, question, patientId, origin: { tab: 'encounter', question } },
      ),
    [start, question, patientId, agent],
  );
  // A free Ask is sent once per press (Codex on #959): leaving the tab unmounts this card, and a remount with the same seq must
  // not run the model again. A chip's agent (the pre-visit's visit planning) runs on each arrival, as designed.
  const askKey = askKeyOf(patientId, seq, question);
  // A remount for a press already sent draws the idle card with its Retry (Codex on #959): the question stays, nothing runs.
  useEffect(() => {
    if (!seq || !question) return;
    if (!agent) {
      if (wasAskSent(askKey)) return;
      markAskSent(askKey);
    }
    ask();
  }, [seq, question, ask, agent, askKey]);
  if (!request) return null;
  const state = cardStateOf(run);
  // Withheld: the engine's plain reason when it is one of the fixed sentences (ask-panel.tsx), else the catalog's line.
  const plain = [...run.events].reverse().find((e) => e.type === 'error')?.plain;
  const reason = state === 'withheld' && typeof plain === 'string' && WITHHELD_PLAIN.includes(plain) ? plain : null;
  const common = { type: agent ? 'brief' : 'ask', title: agent?.title ?? request.question, origin: 'ai' as const, read: [] as string[], produced: askProduced(traceOf(run)) };
  if (state === 'loading') {
    return (
      <>
        <Card {...common} state="loading" step={0} />
        <RetryRow label={ASK_WORDS.stop} onRetry={() => run.cancel()} />
      </>
    );
  }
  // Idle with a question: the run was stopped. Nothing is running, so no busy card; the question can be asked again.
  if (state === 'idle') {
    return (
      <>
        <Card {...common} />
        <RetryRow label={ASK_WORDS.retry} onRetry={ask} />
      </>
    );
  }
  // His one-line states that carry no button of their own; the rest are drawn as the fixed line with the app's Retry.
  const oneLine = state === 'busy' || state === 'timeout' || state === 'down' || state === 'denied' || (state === 'withheld' && !reason);
  const line = reason ?? ASK_STATE_LINE[state];
  const critical = state === 'answered' ? [] : criticalRowsOfRun(run);
  return (
    <>
      {/* His withheld card's "Open the results list" opens the host's results list. */}
      <Card {...common} state={oneLine ? state : 'answered'} onOpenResults={onOpenResults}>
        {state === 'answered' && run.answer ? (
          <>
            {/* Each line keeps its citations (ask-panel.tsx): the source count, with every record id in its accessible name. */}
            {answerBlocks(run.answer).map((b, i) => (
              <Fact key={i}>
                {b.text}
                {b.cites.length ? <span aria-label={`Sources: ${b.cites.join(', ')}`} data-cites={b.cites.join(' ')}>{` · ${b.cites.length === 1 ? '1 source' : `${b.cites.length} sources`}`}</span> : null}
              </Fact>
            ))}
            <Fact>{ASK_WORDS.review}</Fact>
          </>
        ) : !oneLine && line ? (
          <Fact>{line}</Fact>
        ) : null}
        {critical.map((r, i) => (
          <Fact key={`c-${i}`}>{`${r.text} · ${WORDS.criticalTag}`}</Fact>
        ))}
      </Card>
      {state !== 'answered' && state !== 'denied' && state !== 'session-expired' ? <RetryRow label={ASK_WORDS.retry} onRetry={ask} /> : null}
      {/* A failed run (not a withheld or denied answer): the host's fallback (the pre-visit: the code-built brief). */}
      {fallback && state !== 'answered' && state !== 'withheld' && state !== 'denied' ? fallback : null}
    </>
  );
}

// ── The code-built brief (#841: "Prepare", POST /api/intelligence/cards/brief) ─────────────────────────────────
export function CatalogBrief({ patientId, encounterId = null, seq }: { patientId: string; encounterId?: string | null; seq: number }) {
  const b = useBrief({ patientId, encounterId });
  const { load } = b;
  useEffect(() => {
    void load();
  }, [seq, load]);
  if (b.status !== 'done') return <Skeleton className="h-16" aria-label="Loading" />;
  if (!b.card) {
    const line = b.notice ?? SESSION_EXPIRED;
    const state = noticeState(line);
    return (
      <>
        <Card type="brief" title={BRIEF_TITLE} origin="record" read={[]} produced={NOTHING_PRODUCED} state={state ?? 'answered'}>
          {state ? null : <Fact>{line}</Fact>}
        </Card>
        {line !== SESSION_EXPIRED ? <RetryRow onRetry={() => void b.load()} /> : null}
      </>
    );
  }
  const card = b.card;
  return (
    <Card
      type="brief"
      title={BRIEF_TITLE}
      time={answerTime(card.asOf) || undefined}
      origin={catalogOrigin(card.origin.kind)}
      read={[card.readLine]}
      produced={{ checks: [], trace: [['Origin', card.origin.label], ['As of', card.asOf], ...(card.auditId ? ([['Audit', card.auditId]] as [string, string][]) : [])] }}
      limits={card.limits.length ? card.limits.map((l) => <Fact key={l}>{l}</Fact>) : undefined}
    >
      {card.sections.map((s) => (
        <Group key={s.id} label={s.title}>
          {/* Each line keeps its record ids: his source marker opens them. */}
          {s.lines.length
            ? s.lines.map((l) =>
                l.cites.length ? (
                  <Fact key={l.text} source={l.cites.join(', ')} detail={citesDetail(l.cites)}>
                    {l.text}
                  </Fact>
                ) : (
                  <Fact key={l.text}>{l.text}</Fact>
                ),
              )
            : s.emptyLine
              ? <Fact>{s.emptyLine}</Fact>
              : null}
        </Group>
      ))}
    </Card>
  );
}

// ── Explain ──────────────────────────────────────────────────────────────────────────────────────────────────
export function CatalogExplain({ patientId, encounterId = null, appointmentId = null, request, onOpenResults }: { patientId: string; encounterId?: string | null; appointmentId?: string | null; request: ExplainRequest; onOpenResults?: () => void }) {
  // #924: the appointment id goes beside the encounter id (the hook sends it only when there is one), so the engine can
  // confirm a just-arrived encounter.
  const ex = useExplain({ patientId, encounterId, appointmentId });
  const { run, cancel } = ex;
  const { system, seq } = request;
  useEffect(() => {
    // Codex (#954): a system picked while another loads replaces it; else the hook's one-in-flight rule dropped the pick.
    cancel();
    void run(system);
  }, [system, seq, run, cancel]);
  const loading = ex.status === 'loading';
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    if (!loading) return;
    const t = setTimeout(() => setSlow(true), CANCEL_AFTER_MS);
    return () => {
      clearTimeout(t);
      setSlow(false);
    };
  }, [loading]);
  const again = () => void run(system);
  if (loading) {
    return (
      <>
        <Card type="explain" title={systemLabel(system)} origin="ai" read={[]} produced={NOTHING_PRODUCED} state="loading" step={0} />
        {slow ? <RetryRow label="Cancel" onRetry={cancel} /> : null}
        {/* A retry keeps the explanation it replaces on screen (explain-panel.tsx): its critical rows never disappear while it loads. */}
        {ex.explanation ? <ExplainCardOf x={ex.explanation} onOpenResults={onOpenResults} onRetry={again} /> : null}
      </>
    );
  }
  if (ex.notice) {
    const state = noticeState(ex.notice);
    return (
      <>
        <Card type="explain" title={systemLabel(system)} origin="ai" read={[]} produced={NOTHING_PRODUCED} state={state ?? 'answered'}>
          {state ? null : <Fact>{ex.notice}</Fact>}
        </Card>
        {ex.notice !== SESSION_EXPIRED ? <RetryRow onRetry={again} /> : null}
      </>
    );
  }
  return ex.explanation ? <ExplainCardOf x={ex.explanation} onOpenResults={onOpenResults} onRetry={again} /> : null;
}

function ExplainCardOf({ x, onOpenResults, onRetry }: { x: ExplanationV1; onOpenResults?: () => void; onRetry: () => void }) {
  // not-enabled: no card at all (R4-2); duplicate-request: the hook keeps the earlier card.
  if (x.reason === 'not-enabled' || x.reason === 'duplicate-request') return null;
  const title = x.title ?? 'Scrips Intelligence';
  const common = { type: 'explain', title, time: answerTime(x.answeredAt) || undefined, origin: catalogOrigin(x.origin?.kind), read: x.readLines ?? [], produced: explainProduced(x) } as const;
  const openResults = onOpenResults ? (
    <Actions>
      <Button variant="quiet" size="sm" data-action="open-results" onClick={onOpenResults}>
        {RAIL_WORDS.openResults}
      </Button>
    </Actions>
  ) : undefined;
  if (x.state !== 'answered') {
    const state = explainState(x);
    if (state) {
      return (
        <>
          <Card {...common} state={state} />
          {x.retryable && state !== 'denied' && state !== 'consent' ? <RetryRow onRetry={onRetry} /> : null}
        </>
      );
    }
    // Withheld, or failed with its critical rows: the engine's own line verbatim, then the critical rows (X5), then the
    // full limits (the OFF card keeps its Limits disclosure on a withheld card too). A failed card with no engine line says
    // why with the state copy for its reason, as explain-card.tsx does.
    const line = x.state === 'withheld' ? (x.withheldLine ?? null) : (x.withheldLine ?? (x.reason ? STATE_LINE[x.reason] : STATE_LINE.technical));
    return (
      <>
        <Card {...common} actions={openResults} limits={limitsOf([], x.limitsFull)}>
          {line ? <Fact>{line}</Fact> : null}
          {criticalRowsOf(x).length ? (
            <ResultTable>
              {criticalRowsOf(x).map((r) => (
                <ResultRow key={r.source.recordId} row={r} />
              ))}
            </ResultTable>
          ) : null}
        </Card>
        {x.state === 'failed' && x.retryable ? <RetryRow onRetry={onRetry} /> : null}
      </>
    );
  }
  const v = answerView(x);
  const d = x.display;
  const vitals = d?.vitals;
  return (
    <Card {...common} limits={limitsOf(v.limitNotes, x.limitsFull)} actions={openResults}>
      {/* D-5275: the display line (read time, age, sex), shown with the Read lines; display only. */}
      {d ? <Fact>{`Read ${d45(d.readAt.date)}, ${d.readAt.time} · ${'years' in d.age ? `${d.age.years} y` : d.age.text} · ${'recorded' in d.sex ? d.sex.recorded : d.sex.text}`}</Fact> : null}
      {v.headline ? <Fact>{v.headline}</Fact> : null}
      {v.countLines.map((t) => (
        <Fact key={t}>{t}</Fact>
      ))}
      {/* His results table: the Value · Lab flag · Ref range header over the rows (the cells carry the same names). */}
      {v.results.length ? (
        <ResultTable>
          {v.results.map((r) => (
            <div key={r.source.recordId} data-slot="explain-result">
              <ResultRow row={r} />
              {r.subRow ? <ResultRow row={r.subRow} sub /> : null}
            </div>
          ))}
        </ResultTable>
      ) : null}
      {v.resultNotes.map((t) => (
        <Fact key={t}>{t}</Fact>
      ))}
      {v.problems.length ? (
        <Group label={RAIL_WORDS.problemsLabel} fold={v.problems.length}>
          {v.problems.map((p) => (
            <Fact key={p.source.recordId} source={RAIL_WORDS.problemsLabel} detail={recordItemOf(p.sentence, p.source)}>
              {p.sentence}
            </Fact>
          ))}
        </Group>
      ) : null}
      {v.gaps.map((t) => (
        <Fact key={t}>{t}</Fact>
      ))}
      {vitals ? (
        <Group label={vitals.label} fold={vitals.status === 'read' ? vitals.readings.length : 1}>
          {vitals.status === 'read' ? (
            <>
              {vitals.readings.map((r) => (
                <Fact key={r.sourceId}>{[r.text, r.date ? d45(r.date) : null, r.time, r.tag || null, r.measurementSource].filter(Boolean).join(' · ')}</Fact>
              ))}
              <Fact>{vitals.window}</Fact>
              {vitals.notYetShown ? <Fact>{vitals.notYetShown}</Fact> : null}
            </>
          ) : (
            <Fact>{vitals.reason}</Fact>
          )}
        </Group>
      ) : null}
    </Card>
  );
}

/** One result row as his LabRow, with its series under it (D, L8): every observation, plotted or not, and the engine's
 *  range-change note. The source marker opens the record item (labResultOf). */
function ResultRow({ row, sub }: { row: ResultRowView; sub?: boolean }) {
  const t = row.trend;
  return (
    <LabRow r={labResultOf(row)} sub={sub}>
      {t ? (
        <Group label={t.label} fold={t.observations.length}>
          {t.observations.map((o, i) => (
            <Fact key={`${i}-${o}`}>{o}</Fact>
          ))}
        </Group>
      ) : null}
      {t?.rangeNote ? <Fact>{t.rangeNote}</Fact> : null}
    </LabRow>
  );
}

/** The Limits row: the engine's at-rest lines, then its full lists under one fold (his Group), as the OFF card's Limits disclosure. */
function limitsOf(lines: string[], full: ExplanationV1['limitsFull']): ReactNode {
  const groups = limitsGroupsOf(full);
  if (!lines.length && !groups.length) return undefined;
  return (
    <>
      {lines.map((l) => (
        <Fact key={l}>{l}</Fact>
      ))}
      {groups.length ? (
        <Group label="Limits" fold={groups.reduce((n, g) => n + g.items.length, 0)}>
          {groups.map((g) => (
            <Group key={g.head} label={g.head}>
              {g.items.map((i) => (
                <Fact key={i}>{i}</Fact>
              ))}
            </Group>
          ))}
        </Group>
      ) : null}
    </>
  );
}
